import React, { useEffect, useRef, useState } from 'react';
import { SEOHelmet, BreadcrumbList } from 'components/SEO';
import { ViewModeDropdown } from 'components/ViewModeDropdown';
import type { ViewOption } from 'components/ViewModeDropdown';
import { useGetActivityStatsQuery } from 'store/services/activity/activityApi';
import { useActivityFeed } from 'hooks/useActivityFeed';
import LivePulseDashboard from './components/LivePulseDashboard';
import ActivityFilterBar from './components/ActivityFilterBar';
import ActivityFeed from './components/ActivityFeed';
import './ActivityPage.css';

export type ActivityViewMode = 'images-S' | 'images-M' | 'images-L' | 'list';

const VIEW_OPTIONS: ViewOption<ActivityViewMode>[] = [
  { value: 'images-S', label: 'Images (S)' },
  { value: 'images-M', label: 'Images (M)' },
  { value: 'images-L', label: 'Images (L)' },
  { value: 'list', label: 'List' },
];

const VIEW_MODE_STORAGE_KEY = 'activity-view-mode';

const getInitialViewMode = (): ActivityViewMode => {
  const saved = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
  if (saved && VIEW_OPTIONS.some(o => o.value === saved)) {
    return saved as ActivityViewMode;
  }
  return 'images-M';
};

const ActivityPage: React.FC = () => {
  const [viewMode, setViewMode] = useState<ActivityViewMode>(getInitialViewMode);

  const handleViewModeChange = (mode: ActivityViewMode) => {
    setViewMode(mode);
    localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
  };

  const {
    activities,
    isLoading,
    hasMore,
    loadMore,
    isPaused,
    togglePause,
    pendingCount,
    activeTypes,
    toggleType,
    isSocketConnected,
    newItemIds,
    clearNewItem,
    isFetchingMore,
    scrolledPendingCount,
    showBuffered,
    onScroll,
  } = useActivityFeed();

  const {
    data: stats,
    isLoading: statsLoading,
  } = useGetActivityStatsQuery(undefined, {
    pollingInterval: isSocketConnected ? 0 : 60000,
  });

  // Bind scroll handler to .main-page-content scroll container
  const scrollBound = useRef(false);
  useEffect(() => {
    const scrollContainer = document.querySelector('.main-page-content');
    if (!scrollContainer || scrollBound.current) return;
    scrollBound.current = true;

    const handler = (e: Event) => {
      onScroll(e as unknown as React.UIEvent<HTMLElement>);
    };
    scrollContainer.addEventListener('scroll', handler, { passive: true });
    return () => {
      scrollContainer.removeEventListener('scroll', handler);
      scrollBound.current = false;
    };
  }, [onScroll]);

  // When user clicks "new items" pill, scroll to top then flush
  const handleShowNew = () => {
    const scrollContainer = document.querySelector('.main-page-content');
    if (scrollContainer) {
      scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
    }
    showBuffered();
  };

  const breadcrumbs = [
    { name: 'Home', url: 'https://trademagic.gg/home' },
    { name: 'Activity', url: 'https://trademagic.gg/home/activity' },
  ];

  return (
    <>
      <SEOHelmet
        title="Activity Feed - TradeMagic"
        description="Watch real-time trading activity across the TradeMagic platform. See what cards are being traded, shipped, and added to want lists."
        canonical="https://trademagic.gg/home/activity"
      />
      <BreadcrumbList breadcrumbs={breadcrumbs} />

      <div className="activity-page">
        <LivePulseDashboard
          stats={stats}
          isLoading={statsLoading}
          isConnected={isSocketConnected}
        />

        <div className="activity-page__toolbar">
          <ActivityFilterBar
            activeTypes={activeTypes}
            toggleType={toggleType}
            isPaused={isPaused}
            togglePause={togglePause}
            pendingCount={pendingCount}
          />
          <ViewModeDropdown
            options={VIEW_OPTIONS}
            value={viewMode}
            onChange={handleViewModeChange}
          />
        </div>

        <ActivityFeed
          activities={activities}
          isLoading={isLoading}
          hasMore={hasMore}
          loadMore={loadMore}
          isFetchingMore={isFetchingMore}
          newItemIds={newItemIds}
          clearNewItem={clearNewItem}
          scrolledPendingCount={scrolledPendingCount}
          onShowNew={handleShowNew}
          viewMode={viewMode}
        />
      </div>
    </>
  );
};

export default ActivityPage;
